import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Plus, Save, ShieldCheck, Trash2 } from "lucide-react"

/**
 * Rollen und ihre Rechte (#27, 25.08.2026).
 *
 * Seit dem 25.08. gab es Rollen, aber kein Rollenkonzept: die Rolle war ein
 * Wort, und im Code stand `if rolle !== "admin"`. Zuordnen ließ sich nichts —
 * entweder jemand durfte alles oder die vier gesperrten Bereiche gar nicht.
 *
 * 🔴 Alle Schutzregeln sitzen im Backend: der Administrator behält immer alle
 * Rechte, das Recht „Zugänge" darf nicht dem letzten Verwalter genommen werden,
 * eine Rolle mit Zugängen wird nicht gelöscht. Diese Seite graut die
 * entsprechenden Bedienelemente nur aus — sie ist Bequemlichkeit, nicht die Sperre.
 */

export type Recht = { schluessel: string; label: string; beschreibung: string }
export type Rolle = {
  schluessel: string
  name: string
  beschreibung: string
  ist_system: boolean
  rechte: string[]
  rechte_fest: boolean
  nutzer: number
}
export type RollenAntwort = { rollen: Rolle[]; katalog: Recht[]; system: string[]; vollzugriff: string }

const feld = {
  fontSize: 13, padding: "6px 9px", borderRadius: 6,
  border: "1px solid var(--border-color)", background: "var(--bg-card)",
  color: "var(--text)",
} as const

export function RollenTab({ melde }: { melde: (art: "ok" | "fehler", text: string) => void }) {
  const qc = useQueryClient()
  const [entwurf, setEntwurf] = useState<Record<string, string[]>>({})
  const [neuOffen, setNeuOffen] = useState(false)
  const [neuSchluessel, setNeuSchluessel] = useState("")
  const [neuName, setNeuName] = useState("")

  const q = useQuery<RollenAntwort>({
    queryKey: ["rollen"],
    queryFn: async () => {
      const r = await fetch("/api/v1/rollen", { credentials: "include" })
      if (!r.ok) throw new Error(r.status === 403
        ? "Dafür fehlt die Berechtigung „Zugänge und Rollen“."
        : `HTTP ${r.status}`)
      return r.json()
    },
  })

  const frisch = () => {
    qc.invalidateQueries({ queryKey: ["rollen"] })
    // Die eigene Navigation hängt an denselben Rechten — wer sich gerade
    // welche gegeben oder genommen hat, soll das sofort sehen.
    qc.invalidateQueries({ queryKey: ["konto"] })
    qc.invalidateQueries({ queryKey: ["portal-users"] })
  }
  const fehler = (e: unknown) => melde("fehler", e instanceof Error ? e.message : String(e))

  const speichern = useMutation({
    mutationFn: async (a: { schluessel: string; rechte: string[] }) => {
      const r = await fetch(`/api/v1/rollen/${encodeURIComponent(a.schluessel)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ rechte: a.rechte }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      return a
    },
    onSuccess: (a) => {
      setEntwurf((e) => { const n = { ...e }; delete n[a.schluessel]; return n })
      melde("ok", `Rechte der Rolle „${a.schluessel}“ gespeichert.`)
      frisch()
    },
    onError: fehler,
  })

  const anlegen = useMutation({
    mutationFn: async () => {
      const r = await fetch("/api/v1/rollen", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          schluessel: neuSchluessel.trim().toLowerCase(),
          name: neuName.trim() || neuSchluessel.trim(),
          rechte: [],
        }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      return d
    },
    onSuccess: () => {
      melde("ok", `Rolle „${neuName || neuSchluessel}“ angelegt — jetzt die Rechte setzen.`)
      setNeuSchluessel(""); setNeuName(""); setNeuOffen(false)
      frisch()
    },
    onError: fehler,
  })

  const loeschen = useMutation({
    mutationFn: async (schluessel: string) => {
      const r = await fetch(`/api/v1/rollen/${encodeURIComponent(schluessel)}`, {
        method: "DELETE", credentials: "include",
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      return schluessel
    },
    onSuccess: (s) => { melde("ok", `Rolle „${s}“ gelöscht.`); frisch() },
    onError: fehler,
  })

  if (q.isError) return <div style={{ fontSize: 13, color: "var(--destructive)" }}>{String(q.error)}</div>
  if (q.isLoading || !q.data) return <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Lädt …</div>

  const katalog = q.data.katalog

  return (
    <>
      <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 16, maxWidth: 640 }}>
        Was eine Rolle darf. Alles, was hier <strong>nicht</strong> steht, darf
        jeder Angemeldete — Mails lesen, Kontakte, Aufgaben, Kalender. Gesperrt
        wird nur, was Schaden anrichten oder Vertrauliches zeigen kann.
      </p>

      {q.data.rollen.map((rolle) => {
        const gesetzt = entwurf[rolle.schluessel] ?? rolle.rechte
        const geaendert = entwurf[rolle.schluessel] !== undefined
          && JSON.stringify([...gesetzt].sort()) !== JSON.stringify([...rolle.rechte].sort())
        const umschalten = (recht: string) => {
          if (rolle.rechte_fest) return
          const neu = gesetzt.includes(recht)
            ? gesetzt.filter((r) => r !== recht)
            : [...gesetzt, recht]
          setEntwurf((e) => ({ ...e, [rolle.schluessel]: neu }))
        }
        return (
          <section key={rolle.schluessel} style={{
            marginBottom: 14, padding: 14, borderRadius: 8,
            border: "1px solid var(--border-color)", background: "var(--bg-card)",
          }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <strong style={{ fontSize: 14 }}>{rolle.name}</strong>
              <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                {rolle.schluessel}
                {rolle.ist_system && " · Systemrolle"}
                {` · ${rolle.nutzer} ${rolle.nutzer === 1 ? "Zugang" : "Zugänge"}`}
              </span>
              {!rolle.ist_system && (
                <button
                  type="button"
                  title={rolle.nutzer
                    ? "Erst die Zugänge umtragen — sonst hängen sie an einer Rolle, die es nicht mehr gibt"
                    : "Rolle löschen"}
                  disabled={rolle.nutzer > 0 || loeschen.isPending}
                  onClick={() => loeschen.mutate(rolle.schluessel)}
                  style={{ ...feld, marginLeft: "auto", fontSize: 12, padding: "3px 8px",
                           cursor: rolle.nutzer ? "not-allowed" : "pointer",
                           opacity: rolle.nutzer ? 0.45 : 1,
                           color: "var(--destructive)", display: "inline-flex",
                           alignItems: "center", gap: 4 }}
                >
                  <Trash2 size={12} /> Löschen
                </button>
              )}
            </div>

            {rolle.beschreibung && (
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                {rolle.beschreibung}
              </div>
            )}

            <div style={{ marginTop: 10, display: "grid", gap: 7 }}>
              {katalog.map((recht) => (
                <label key={recht.schluessel} style={{
                  display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5,
                  cursor: rolle.rechte_fest ? "not-allowed" : "pointer",
                  opacity: rolle.rechte_fest ? 0.6 : 1,
                }}>
                  <input
                    type="checkbox"
                    checked={gesetzt.includes(recht.schluessel)}
                    disabled={rolle.rechte_fest}
                    onChange={() => umschalten(recht.schluessel)}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    <strong style={{ fontWeight: 500 }}>{recht.label}</strong>
                    <span style={{ color: "var(--text-muted)" }}> — {recht.beschreibung}</span>
                  </span>
                </label>
              ))}
            </div>

            {rolle.rechte_fest ? (
              <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 9,
                            display: "flex", gap: 5, alignItems: "center" }}>
                <ShieldCheck size={12} />
                Nicht abwählbar — sonst könnte sich die Verwaltung selbst aussperren.
              </div>
            ) : (
              <button
                type="button"
                disabled={!geaendert || speichern.isPending}
                onClick={() => speichern.mutate({ schluessel: rolle.schluessel, rechte: gesetzt })}
                style={{
                  marginTop: 10, display: "inline-flex", alignItems: "center", gap: 5,
                  fontSize: 12.5, fontWeight: 500, padding: "5px 11px", borderRadius: 6,
                  border: "none",
                  background: geaendert ? "#3C3489" : "rgba(44,44,42,0.12)",
                  color: geaendert ? "#fff" : "var(--text-muted)",
                  cursor: geaendert ? "pointer" : "not-allowed",
                }}
              >
                <Save size={13} /> {geaendert ? "Rechte speichern" : "Gespeichert"}
              </button>
            )}
          </section>
        )
      })}

      {neuOffen ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (/^[a-z0-9_]{2,32}$/.test(neuSchluessel.trim().toLowerCase())) anlegen.mutate()
            else melde("fehler", "Schlüssel: Kleinbuchstaben, Ziffern, Unterstrich (2–32 Zeichen).")
          }}
          style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
                   padding: 12, borderRadius: 8, border: "1px solid var(--border-color)" }}
        >
          <input value={neuName} onChange={(e) => setNeuName(e.target.value)}
                 placeholder="Name, z. B. Werkstudent" style={{ ...feld, flex: "1 1 180px" }} />
          <input value={neuSchluessel} onChange={(e) => setNeuSchluessel(e.target.value)}
                 placeholder="schluessel (klein, ohne Leerzeichen)"
                 style={{ ...feld, flex: "1 1 200px" }} />
          <button type="submit" disabled={anlegen.isPending}
                  style={{ ...feld, cursor: "pointer", background: "#3C3489", color: "#fff",
                           border: "none", fontWeight: 500 }}>
            Anlegen
          </button>
          <button type="button" onClick={() => setNeuOffen(false)}
                  style={{ ...feld, cursor: "pointer" }}>Abbrechen</button>
        </form>
      ) : (
        <button type="button" onClick={() => setNeuOffen(true)}
                style={{ ...feld, cursor: "pointer", display: "inline-flex",
                         alignItems: "center", gap: 5 }}>
          <Plus size={13} /> Neue Rolle
        </button>
      )}
    </>
  )
}
